"use client";

import { StarRating } from "@/components/shared/star-rating";
import { cn } from "@/lib/utils/cn";
import { formatAUM } from "@/lib/utils/format";
import {
  navFreshness,
  veryStaleBannerText,
  type NavFreshnessLevel,
  type NavFreshnessReading,
} from "@/lib/funds/nav-freshness";
import {
  RefreshCw,
  Download,
  Share2,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
} from "lucide-react";

type FundSummaryCardProps = {
  schemeName: string;
  amc: string;
  category: string;
  nav: number;
  navDate: string;
  /** AUM is stored in crores for INR funds, in base currency units otherwise. Null/0 renders as "—". */
  aum: number | null;
  expenseRatio: number;
  morningstarRating: number;
  wtmScore?: number;
  returns: { y1: number | null; y3: number | null; y5: number | null };
  planType: string;
  currency: string;
  generatedAt: string;
  dataMonth: string;
  onRefresh?: () => void;
  onDownloadPdf?: () => void;
  onShare?: () => void;
  /**
   * The instant the NAV's age is judged against (RM-W1-11). Defaults to the render clock; supplied
   * explicitly by tests so the day-3 / day-4 / day-14 / day-15 boundaries can be pinned. The page
   * this card sits on is client-rendered after a fetch, so there is no server render of this branch
   * to disagree with.
   */
  now?: Date;
};

/** Tailwind classes per freshness level. `invalid` is muted, never green. */
const NAV_BADGE_CLASS: Record<NavFreshnessLevel, string> = {
  fresh: "bg-cgreen/15 text-cgreen",
  stale: "bg-gold/15 text-gold",
  very_stale: "bg-cred/15 text-cred",
  invalid: "bg-surface-light/40 text-dim",
};

/**
 * The NAV freshness badge (RM-W1-11, ruling OR-03). The tooltip carries the threshold semantics, per
 * the house rule that every UI metric explains itself.
 */
function NavFreshnessBadge({ reading }: { reading: NavFreshnessReading }) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold",
        NAV_BADGE_CLASS[reading.level]
      )}
      title={reading.tooltip}
      data-testid="nav-freshness-badge"
      data-nav-freshness={reading.level}
    >
      {reading.badgeLabel}
    </span>
  );
}

function ReturnBadge({
  label,
  value,
}: {
  label: string;
  value: number | null;
}) {
  if (value === null) return null;
  const isPositive = value >= 0;
  return (
    <div className="flex flex-col items-center gap-0.5">
      <span className="text-dim text-[10px] uppercase tracking-wide">
        {label}
      </span>
      <span
        className={cn(
          "text-sm font-bold font-tabular flex items-center gap-0.5",
          isPositive ? "text-cgreen" : "text-cred"
        )}
      >
        {isPositive ? (
          <TrendingUp className="w-3 h-3" />
        ) : (
          <TrendingDown className="w-3 h-3" />
        )}
        {value > 0 ? "+" : ""}
        {value.toFixed(1)}%
      </span>
    </div>
  );
}

export function FundSummaryCard({
  schemeName,
  amc,
  category,
  nav,
  navDate,
  aum,
  expenseRatio,
  morningstarRating,
  wtmScore,
  returns,
  planType,
  currency,
  generatedAt,
  dataMonth,
  onRefresh,
  onDownloadPdf,
  onShare,
  now,
}: FundSummaryCardProps) {
  // RM-W1-11: the NAV's age, in Indian civil dates. Computed once per render from the caller's clock.
  const navReading = navFreshness(navDate, now ?? new Date());

  return (
    <>
      {navReading.level === "very_stale" && (
        <div
          className="flex items-start gap-3 bg-cred/10 border border-cred/30 rounded-xl p-4 mb-4"
          role="status"
          data-testid="nav-very-stale-banner"
        >
          <AlertTriangle className="w-4 h-4 text-cred shrink-0 mt-0.5" />
          <p className="text-cred text-xs leading-relaxed m-0">
            {veryStaleBannerText(navReading)}
          </p>
        </div>
      )}
      <div className="relative bg-surface/60 backdrop-blur-md border border-surface-light/30 rounded-2xl p-6 md:p-8">
        {/* Top row: name + actions */}
        <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
          <div className="min-w-0 flex-1">
            <h1 className="text-foreground text-xl md:text-2xl font-serif leading-tight mb-1">
              {schemeName}
            </h1>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-muted-foreground text-sm">{amc}</span>
              <span className="bg-blue/20 text-blue text-[10px] font-semibold px-2 py-0.5 rounded-full">
                {category}
              </span>
              <span className="bg-surface-light/30 text-dim text-[10px] font-semibold px-2 py-0.5 rounded-full">
                {planType}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {onRefresh && (
              <button
                onClick={onRefresh}
                className="p-2 rounded-lg bg-navy-mid text-muted-foreground hover:text-foreground transition border-none cursor-pointer"
                title="Refresh report"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            )}
            {onDownloadPdf && (
              <button
                onClick={onDownloadPdf}
                className="p-2 rounded-lg bg-navy-mid text-muted-foreground hover:text-foreground transition border-none cursor-pointer"
                title="Download PDF"
              >
                <Download className="w-4 h-4" />
              </button>
            )}
            {onShare && (
              <button
                onClick={onShare}
                className="p-2 rounded-lg bg-navy-mid text-muted-foreground hover:text-foreground transition border-none cursor-pointer"
                title="Share"
              >
                <Share2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* WTM Score + Morningstar */}
        <div className="flex items-center gap-6 mb-5 flex-wrap">
          {wtmScore !== undefined && (
            <div className="flex items-center gap-2">
              <span className="text-foreground text-3xl font-bold font-tabular">
                {wtmScore.toFixed(1)}
              </span>
              <div className="flex flex-col">
                <span className="text-dim text-[10px] uppercase tracking-wide">
                  WTM Score
                </span>
                <StarRating score={wtmScore} size="text-sm" />
              </div>
            </div>
          )}
          <div className="flex items-center gap-2">
            <StarRating score={morningstarRating} size="text-sm" />
            <span className="text-dim text-[11px]">Morningstar</span>
          </div>
        </div>

        {/* Key metrics row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
          <div>
            <span className="text-dim text-[10px] uppercase tracking-wide block mb-0.5">
              NAV
            </span>
            <span className="text-foreground text-sm font-bold font-tabular">
              {currency === "INR" ? "\u20B9" : "$"}
              {nav.toFixed(2)}
            </span>
            {/* RM-W1-11: the NAV is never printed without the date it belongs to, and the badge
                says how old that date is. */}
            <span className="text-dim text-[10px] block mt-1">
              NAV as of {navReading.dateLabel}
            </span>
            <span className="block mt-1">
              <NavFreshnessBadge reading={navReading} />
            </span>
          </div>
          <div>
            <span className="text-dim text-[10px] uppercase tracking-wide block mb-0.5">
              AUM
            </span>
            <span className="text-foreground text-sm font-bold font-tabular">
              {formatAUM(aum, currency)}
            </span>
          </div>
          <div>
            <span className="text-dim text-[10px] uppercase tracking-wide block mb-0.5">
              Expense Ratio
            </span>
            <span className="text-foreground text-sm font-bold font-tabular">
              {expenseRatio.toFixed(2)}%
            </span>
          </div>
          <div>
            <span className="text-dim text-[10px] uppercase tracking-wide block mb-0.5">
              Data Month
            </span>
            <span className="text-foreground text-sm font-semibold">
              {dataMonth}
            </span>
          </div>
        </div>

        {/* Returns row */}
        <div className="flex items-center gap-6 pt-4 border-t border-surface-light/20">
          <ReturnBadge label="1Y" value={returns.y1} />
          <ReturnBadge label="3Y" value={returns.y3} />
          <ReturnBadge label="5Y" value={returns.y5} />
        </div>

        {/* Generated timestamp */}
        <div className="text-dim text-[10px] mt-4">
          Generated{" "}
          {new Date(generatedAt).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          })}
        </div>
      </div>
    </>
  );
}
